import axios from 'axios';
import { getAuthToken } from '@/services/auth';
import pLimit from 'p-limit';

const MAX_CONCURRENT_FETCHES = 3; // adjust based on backend tolerance
const limit = pLimit(MAX_CONCURRENT_FETCHES);

/** Only this many journeys are written to SecureLS; full list stays in memory after API refresh. */
export const MAX_PERSISTED_USER_JOURNEYS = 15;

function hasUsableDashboardStructure(j) {
  return j && Array.isArray(j.Structure) && j.Structure.length > 0;
}

/** Keep full Structure from cache when the list endpoint returns summaries (avoids N+1 until needed). */
function mergeJourneyListPreservingStructure(apiList, cachedList) {
  if (!apiList || !Array.isArray(apiList)) return [];
  if (!cachedList || cachedList.length === 0) return apiList;
  const cachedById = new Map(cachedList.map((row) => [row.ID, row]));
  return apiList.map((summary) => {
    const cached = cachedById.get(summary.ID);
    if (!cached || !hasUsableDashboardStructure(cached)) return summary;
    return { ...summary, Structure: cached.Structure, ParticipantProgress: cached.ParticipantProgress, ParticipantProgressPercentage: cached.ParticipantProgressPercentage };
  });
}

// const createEmptyLastOpenedFullJourney = () => ({
//     journeyId: null,
//     journey: null,
//     tenantId: null,
//     cachedAt: null,
// });

const getDefaultState = () => {
    return {
        lastUserJourney: null,
        userJourneys: [],
        lastUpdated: null,
        isParticipantView: false,
        // lastOpenedFullJourney: createEmptyLastOpenedFullJourney(),
    }
};

const convertIsoStringToLocalDateTime = (dateTime) => {
    if (dateTime !== undefined && dateTime !== null && dateTime !== '') {
        const date = new Date(dateTime);
        // Get the date and time components
        const year = date.getFullYear().toString().padStart(4, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        // Format the datetime-local string
        const datetimeLocalString = `${year}-${month}-${day}T${hours}:${minutes}`;

        return datetimeLocalString;
    }
    return null;
};

const prepareJourneyStructureForSave = async function (JourneyID, Structure, TenantID) {

    let contentNewImages = [];

    let journeyLinkedAssets = [];

    let droppedDropzoneElements = [];

    Structure.forEach((StructureItem, structureIndex) => {

        if (StructureItem.Name === undefined || StructureItem.Name === null || StructureItem.Name === '') {
            StructureItem.Name = '#';
        }

        // check chapters for new images
        if (StructureItem.Type === 'Chapter') {

            if (StructureItem.hasOwnProperty('Units')) {

                //before save always eliminate the dummy last unit
                if (StructureItem.Units.length > 1) {
                    StructureItem.Units = StructureItem.Units.filter((unit) => !unit.hasOwnProperty('Temp'));
                }
                if (StructureItem.Units.length === 1) {
                    delete StructureItem.Units[0].Temp;
                }

                StructureItem.Units.forEach((Unit, indexUnit) => {

                    if (Unit.hasOwnProperty('LinkedAssets')) {

                        Unit.LinkedAssets.forEach((linkedAsset, indexLinkedAsset) => {

                            if (!StructureItem.hasOwnProperty('SourceJourneyID') && !Unit.hasOwnProperty('SourceJourneyID')) {

                                if (linkedAsset.Type === 'Attachment' && linkedAsset.hasOwnProperty('IsNew')) {

                                    let fileObject = {
                                        name: linkedAsset.Name,
                                        ext: linkedAsset.Ext,
                                        type: linkedAsset.File_type,
                                        file: linkedAsset.File,
                                        folder_name: `journey/${JourneyID}/assets`,
                                    }

                                    Unit.LinkedAssets[indexLinkedAsset] = {
                                        Type: linkedAsset.Type,
                                        Name: `${linkedAsset.Name}.${linkedAsset.Ext}`,
                                        Label: linkedAsset.Label,
                                        IsTreasureChestAsset: linkedAsset.IsTreasureChestAsset,
                                        FolderName: `journey/${JourneyID}/assets`
                                    }

                                    journeyLinkedAssets.push(fileObject);
                                }
                                if (linkedAsset.Type === 'MemoryCard') {
                                    let memoryCardSaveObject = {
                                        Type: linkedAsset.Type,
                                        BatchNumber: linkedAsset.BatchNumber,
                                        MemoryCardNumber: linkedAsset.MemoryCardNumber,
                                        IsTreasureChestAsset: linkedAsset.IsTreasureChestAsset,
                                    }
                                    Unit.LinkedAssets[indexLinkedAsset] = memoryCardSaveObject;
                                }
                                if (linkedAsset.Type === 'ExpeditionLog') {
                                    let expeditionLogSaveObject = {
                                        Type: linkedAsset.Type,
                                        ExpeditionLogNumber: linkedAsset.ExpeditionLogNumber,
                                        IsTreasureChestAsset: linkedAsset.IsTreasureChestAsset,
                                    }
                                    Unit.LinkedAssets[indexLinkedAsset] = expeditionLogSaveObject;
                                }

                            }
                            else {

                                let sourceJourneyID = Unit.hasOwnProperty('SourceJourneyID') ? Unit.SourceJourneyID : StructureItem.SourceJourneyID;

                                if (sourceJourneyID !== JourneyID) {

                                    // let copyObject = {
                                    //     source_key: `uploads/${TenantID}/journey/${sourceJourneyID}/assets/${linkedAsset.Name}`,
                                    //     folder_name: `journey/${JourneyID}/assets`,
                                    //     file_name: linkedAsset.Name,
                                    // }
                                    // droppedDropzoneElements.push(copyObject);
                                    // StructureItem.Units[indexUnit].LinkedAssets[indexLinkedAsset].FolderName = copyObject.folder_name;

                                    if (linkedAsset.Type === 'Attachment') {

                                        let copyObject = {
                                            source_key: `uploads/${TenantID}/journey/${sourceJourneyID}/assets/${linkedAsset.Name}`,
                                            folder_name: `journey/${JourneyID}/assets`,
                                            file_name: linkedAsset.Name,
                                        }
                                        droppedDropzoneElements.push(copyObject);
                                        Unit.LinkedAssets[indexLinkedAsset].FolderName = copyObject.folder_name;

                                    }

                                    if (linkedAsset.Type === 'MemoryCard') {
                                        let memoryCardSaveObject = {
                                            Type: linkedAsset.Type,
                                            BatchNumber: linkedAsset.BatchNumber,
                                            MemoryCardNumber: linkedAsset.MemoryCardNumber,
                                            IsTreasureChestAsset: linkedAsset.IsTreasureChestAsset,
                                        }
                                        Unit.LinkedAssets[indexLinkedAsset] = memoryCardSaveObject;
                                    }

                                    if (linkedAsset.Type === 'ExpeditionLog') {
                                        let expeditionLogSaveObject = {
                                            Type: linkedAsset.Type,
                                            ExpeditionLogNumber: linkedAsset.ExpeditionLogNumber,
                                            IsTreasureChestAsset: linkedAsset.IsTreasureChestAsset,
                                        }
                                        Unit.LinkedAssets[indexLinkedAsset] = expeditionLogSaveObject;
                                    }

                                }

                            }

                        });
                    }

                    if (Unit.hasOwnProperty('Blocks')) {
                        Unit.Blocks.forEach((Block) => {
                            if (Block.hasOwnProperty('Elements')) {
                                Block.Elements.forEach((Element) => {

                                    if (!StructureItem.hasOwnProperty('SourceJourneyID') && !Unit.hasOwnProperty('SourceJourneyID')) {
                                        if (Element.type === 'image' && Element.data.file.hasOwnProperty('isNew')) {

                                            let imageObject = {
                                                element_type: 'image',
                                                name: Element.data.file.name,
                                                ext: Element.data.file.ext,
                                                type: Element.data.file.type,
                                                file: Element.data.file.file,
                                                folder_name: `journey/${JourneyID}/assets`,
                                            }

                                            const imageName = `${Element.data.file.name}.${Element.data.file.ext}`;

                                            Element.data.file = {
                                                name: imageName
                                            }
                                            contentNewImages.push(imageObject);
                                        }

                                        if (Element.type === 'image' && !Element.data.file.hasOwnProperty('isNew') && Element.data.file.hasOwnProperty('url')) {
                                            delete Element.data.file.url;
                                        }

                                        if (Element.type === 'reusableImage' && Element.data.file.hasOwnProperty('isNew')) {

                                            let reusableImageObject = {
                                                element_type: 'reusableImage',
                                                source_key: Element.data.file.source_key,
                                                file_name: Element.data.file.name,
                                                folder_name: `journey/${JourneyID}/assets`,
                                            }

                                            Element.data.file = {
                                                name: reusableImageObject.file_name
                                            }
                                            contentNewImages.push(reusableImageObject);
                                        }

                                        if (Element.type === 'reusableImage' && !Element.data.file.hasOwnProperty('isNew') && Element.data.file.hasOwnProperty('url')) {
                                            delete Element.data.file.url;
                                        }

                                        if (Element.type === 'columns' && Element.data && Element.data.columns) {

                                            for (let Column of Element.data.columns) {

                                                // parse editor data for columns to avoid nesting error
                                                if (typeof Column.editorData === 'string') {
                                                    Column.editorData = JSON.parse(Column.editorData);
                                                }

                                                for (let ColumnElement of Column.editorData.blocks) {

                                                    if (ColumnElement.type === 'image' && ColumnElement.data.file.hasOwnProperty('isNew')) {

                                                        let imageObject = {
                                                            element_type: 'image',
                                                            name: ColumnElement.data.file.name,
                                                            ext: ColumnElement.data.file.ext,
                                                            type: ColumnElement.data.file.type,
                                                            file: ColumnElement.data.file.file,
                                                            folder_name: `journey/${JourneyID}/assets`,
                                                        }

                                                        const imageName = `${ColumnElement.data.file.name}.${ColumnElement.data.file.ext}`;

                                                        ColumnElement.data.file = {
                                                            name: imageName
                                                        }
                                                        contentNewImages.push(imageObject);
                                                    }

                                                    if (ColumnElement.type === 'image' && !ColumnElement.data.file.hasOwnProperty('isNew') && ColumnElement.data.file.hasOwnProperty('url')) {
                                                        delete ColumnElement.data.file.url;
                                                    }

                                                    if (ColumnElement.type === 'reusableImage' && ColumnElement.data.file.hasOwnProperty('isNew')) {

                                                        let reusableImageObject = {
                                                            element_type: 'reusableImage',
                                                            source_key: ColumnElement.data.file.source_key,
                                                            file_name: ColumnElement.data.file.name,
                                                            folder_name: `journey/${JourneyID}/assets`,
                                                        }

                                                        ColumnElement.data.file = {
                                                            name: reusableImageObject.file_name
                                                        }
                                                        contentNewImages.push(reusableImageObject);
                                                    }

                                                    if (ColumnElement.type === 'reusableImage' && !ColumnElement.data.file.hasOwnProperty('isNew') && ColumnElement.data.file.hasOwnProperty('url')) {
                                                        delete ColumnElement.data.file.url;
                                                    }

                                                }

                                                // serialize editor data for columns to avoid nesting error
                                                if (typeof Column.editorData !== 'string') {
                                                    Column.editorData = JSON.stringify(Column.editorData);
                                                }

                                            }
                                        }
                                    }
                                    else {

                                        if (Element.type === 'image' && Element.data.file.hasOwnProperty('url')) {
                                            delete Element.data.file.url;
                                        }

                                        if (Element.type === 'reusableImage' && Element.data.file.hasOwnProperty('url')) {
                                            delete Element.data.file.url;
                                        }

                                        if (Element.type === 'columns' && Element.data && Element.data.columns) {

                                            for (let Column of Element.data.columns) {

                                                // parse editor data for columns to avoid nesting error
                                                if (typeof Column.editorData === 'string') {
                                                    Column.editorData = JSON.parse(Column.editorData);
                                                }

                                                for (let ColumnElement of Column.editorData.blocks) {

                                                    if (ColumnElement.type === 'image' && ColumnElement.data.file.hasOwnProperty('url')) {
                                                        delete ColumnElement.data.file.url;
                                                    }

                                                    if (ColumnElement.type === 'reusableImage' && ColumnElement.data.file.hasOwnProperty('url')) {
                                                        delete ColumnElement.data.file.url;
                                                    }

                                                }

                                                // serialize editor data for columns to avoid nesting error
                                                if (typeof Column.editorData !== 'string') {
                                                    Column.editorData = JSON.stringify(Column.editorData);
                                                }

                                            }
                                        }


                                        let sourceJourneyID = Unit.hasOwnProperty('SourceJourneyID') ? Unit.SourceJourneyID : StructureItem.SourceJourneyID;

                                        if (sourceJourneyID !== JourneyID) {

                                            if (Element.type === 'image') {
                                                let copyObject = {
                                                    source_key: `uploads/${TenantID}/journey/${sourceJourneyID}/assets/${Element.data.file.name}`,
                                                    folder_name: `journey/${JourneyID}/assets`,
                                                    file_name: Element.data.file.name,
                                                }
                                                droppedDropzoneElements.push(copyObject);
                                            }

                                            if (Element.type === 'reusableImage') {
                                                let copyObject = {
                                                    source_key: `uploads/${TenantID}/journey/${sourceJourneyID}/assets/${Element.data.file.name}`,
                                                    folder_name: `journey/${JourneyID}/assets`,
                                                    file_name: Element.data.file.name,
                                                }
                                                droppedDropzoneElements.push(copyObject);
                                            }

                                            if (Element.type === 'columns' && Element.data && Element.data.columns) {

                                                for (let Column of Element.data.columns) {

                                                    // parse editor data for columns to avoid nesting error
                                                    if (typeof Column.editorData === 'string') {
                                                        Column.editorData = JSON.parse(Column.editorData);
                                                    }

                                                    for (let ColumnElement of Column.editorData.blocks) {

                                                        if (ColumnElement.type === 'image') {
                                                            let copyObject = {
                                                                source_key: `uploads/${TenantID}/journey/${sourceJourneyID}/assets/${ColumnElement.data.file.name}`,
                                                                folder_name: `journey/${JourneyID}/assets`,
                                                                file_name: ColumnElement.data.file.name,
                                                            }
                                                            droppedDropzoneElements.push(copyObject);
                                                        }

                                                        if (ColumnElement.type === 'reusableImage') {
                                                            let copyObject = {
                                                                source_key: `uploads/${TenantID}/journey/${sourceJourneyID}/assets/${ColumnElement.data.file.name}`,
                                                                folder_name: `journey/${JourneyID}/assets`,
                                                                file_name: ColumnElement.data.file.name,
                                                            }
                                                            droppedDropzoneElements.push(copyObject);
                                                        }

                                                    }

                                                    // serialize editor data for columns to avoid nesting error
                                                    if (typeof Column.editorData !== 'string') {
                                                        Column.editorData = JSON.stringify(Column.editorData);
                                                    }

                                                }
                                            }

                                        }
                                    }

                                });
                            }
                        });
                    }

                    delete Unit.SourceJourneyID;

                });

            }

            delete StructureItem.SourceJourneyID;

        }

        // convert periods to universal time
        // check events for modules with unlock time
        if (StructureItem.Type === 'Event') {

            if (StructureItem.Options.IsUnlock && StructureItem.Periods.Unlock !== null) {
                StructureItem.Periods.Unlock = new Date(StructureItem.Periods.Unlock).toISOString();
            }

            if (StructureItem.Options.IsMeeting && StructureItem.Periods.From !== null) {
                StructureItem.Periods.From = new Date(StructureItem.Periods.From).toISOString();
            }

            if (StructureItem.Options.IsMeeting && StructureItem.Periods.To !== null) {
                StructureItem.Periods.To = new Date(StructureItem.Periods.To).toISOString();
            }

            if (StructureItem.hasOwnProperty('LinkedAssets')) {

                StructureItem.LinkedAssets.forEach((linkedAsset, indexLinkedAsset) => {

                    if (!StructureItem.hasOwnProperty('SourceJourneyID')) {

                        if (linkedAsset.Type === 'Attachment' && linkedAsset.hasOwnProperty('IsNew')) {

                            let fileObject = {
                                name: linkedAsset.Name,
                                ext: linkedAsset.Ext,
                                type: linkedAsset.File_type,
                                file: linkedAsset.File,
                                folder_name: `journey/${JourneyID}/assets`,
                            }

                            StructureItem.LinkedAssets[indexLinkedAsset] = {
                                Type: linkedAsset.Type,
                                Name: `${linkedAsset.Name}.${linkedAsset.Ext}`,
                                Label: linkedAsset.Label,
                                IsTreasureChestAsset: linkedAsset.IsTreasureChestAsset,
                                FolderName: `journey/${JourneyID}/assets`
                            }

                            journeyLinkedAssets.push(fileObject);
                        }

                        if (linkedAsset.Type === 'MemoryCard') {
                            let memoryCardSaveObject = {
                                Type: linkedAsset.Type,
                                BatchNumber: linkedAsset.BatchNumber,
                                MemoryCardNumber: linkedAsset.MemoryCardNumber,
                                IsTreasureChestAsset: linkedAsset.IsTreasureChestAsset,
                            }
                            StructureItem.LinkedAssets[indexLinkedAsset] = memoryCardSaveObject;
                        }

                        if (linkedAsset.Type === 'ExpeditionLog') {
                            let expeditionLogSaveObject = {
                                Type: linkedAsset.Type,
                                ExpeditionLogNumber: linkedAsset.ExpeditionLogNumber,
                                IsTreasureChestAsset: linkedAsset.IsTreasureChestAsset,
                            }
                            StructureItem.LinkedAssets[indexLinkedAsset] = expeditionLogSaveObject;
                        }

                    }
                    else {

                        if (StructureItem.SourceJourneyID !== JourneyID) {


                            if (linkedAsset.Type === 'Attachment') {

                                let copyObject = {
                                    source_key: `uploads/${TenantID}/journey/${StructureItem.SourceJourneyID}/assets/${linkedAsset.Name}`,
                                    folder_name: `journey/${JourneyID}/assets`,
                                    file_name: linkedAsset.Name,
                                }
                                droppedDropzoneElements.push(copyObject);
                                StructureItem.LinkedAssets[indexLinkedAsset].FolderName = copyObject.folder_name;

                            }

                            if (linkedAsset.Type === 'MemoryCard') {
                                let memoryCardSaveObject = {
                                    Type: linkedAsset.Type,
                                    BatchNumber: linkedAsset.BatchNumber,
                                    MemoryCardNumber: linkedAsset.MemoryCardNumber,
                                    IsTreasureChestAsset: linkedAsset.IsTreasureChestAsset,
                                }
                                StructureItem.LinkedAssets[indexLinkedAsset] = memoryCardSaveObject;
                            }

                            if (linkedAsset.Type === 'ExpeditionLog') {
                                let expeditionLogSaveObject = {
                                    Type: linkedAsset.Type,
                                    ExpeditionLogNumber: linkedAsset.ExpeditionLogNumber,
                                    IsTreasureChestAsset: linkedAsset.IsTreasureChestAsset,
                                }
                                StructureItem.LinkedAssets[indexLinkedAsset] = expeditionLogSaveObject;
                            }

                        }
                    }
                });
            }

            if (StructureItem.hasOwnProperty('Details') && StructureItem.Details.hasOwnProperty('Elements') && StructureItem.Details.Elements.length > 0) {
                StructureItem.Details.Elements.forEach((Element) => {

                    if (!StructureItem.hasOwnProperty('SourceJourneyID')) {
                        if (Element.type === 'image' && Element.data.file.hasOwnProperty('isNew')) {

                            let imageObject = {
                                element_type: 'image',
                                name: Element.data.file.name,
                                ext: Element.data.file.ext,
                                type: Element.data.file.type,
                                file: Element.data.file.file,
                                folder_name: `journey/${JourneyID}/assets`,
                            }

                            const imageName = `${Element.data.file.name}.${Element.data.file.ext}`;

                            Element.data.file = {
                                name: imageName
                            }
                            contentNewImages.push(imageObject);
                        }

                        if (Element.type === 'image' && !Element.data.file.hasOwnProperty('isNew') && Element.data.file.hasOwnProperty('url')) {
                            delete Element.data.file.url;
                        }

                        if (Element.type === 'reusableImage' && Element.data.file.hasOwnProperty('isNew')) {

                            let reusableImageObject = {
                                element_type: 'reusableImage',
                                source_key: Element.data.file.source_key,
                                file_name: Element.data.file.name,
                                folder_name: `journey/${JourneyID}/assets`,
                            }

                            Element.data.file = {
                                name: reusableImageObject.file_name
                            }
                            contentNewImages.push(reusableImageObject);
                        }

                        if (Element.type === 'reusableImage' && !Element.data.file.hasOwnProperty('isNew') && Element.data.file.hasOwnProperty('url')) {
                            delete Element.data.file.url;
                        }

                        if (Element.type === 'columns' && Element.data && Element.data.columns) {

                            for (let Column of Element.data.columns) {

                                // parse editor data for columns to avoid nesting error
                                if (typeof Column.editorData === 'string') {
                                    Column.editorData = JSON.parse(Column.editorData);
                                }

                                for (let ColumnElement of Column.editorData.blocks) {

                                    if (ColumnElement.type === 'image' && ColumnElement.data.file.hasOwnProperty('isNew')) {

                                        let imageObject = {
                                            element_type: 'image',
                                            name: ColumnElement.data.file.name,
                                            ext: ColumnElement.data.file.ext,
                                            type: ColumnElement.data.file.type,
                                            file: ColumnElement.data.file.file,
                                            folder_name: `journey/${JourneyID}/assets`,
                                        }

                                        const imageName = `${ColumnElement.data.file.name}.${ColumnElement.data.file.ext}`;

                                        ColumnElement.data.file = {
                                            name: imageName
                                        }
                                        contentNewImages.push(imageObject);
                                    }

                                    if (ColumnElement.type === 'image' && !ColumnElement.data.file.hasOwnProperty('isNew') && ColumnElement.data.file.hasOwnProperty('url')) {
                                        delete ColumnElement.data.file.url;
                                    }

                                    if (ColumnElement.type === 'reusableImage' && ColumnElement.data.file.hasOwnProperty('isNew')) {

                                        let reusableImageObject = {
                                            element_type: 'reusableImage',
                                            source_key: ColumnElement.data.file.source_key,
                                            file_name: ColumnElement.data.file.name,
                                            folder_name: `journey/${JourneyID}/assets`,
                                        }

                                        ColumnElement.data.file = {
                                            name: reusableImageObject.file_name
                                        }
                                        contentNewImages.push(reusableImageObject);
                                    }

                                    if (ColumnElement.type === 'reusableImage' && !ColumnElement.data.file.hasOwnProperty('isNew') && ColumnElement.data.file.hasOwnProperty('url')) {
                                        delete ColumnElement.data.file.url;
                                    }

                                }

                                // serialize editor data for columns to avoid nesting error
                                if (typeof Column.editorData !== 'string') {
                                    Column.editorData = JSON.stringify(Column.editorData);
                                }

                            }
                        }

                    }

                    else {

                        if (Element.type === 'image' && Element.data.file.hasOwnProperty('url')) {
                            delete Element.data.file.url;
                        }

                        if (Element.type === 'reusableImage' && Element.data.file.hasOwnProperty('url')) {
                            delete Element.data.file.url;
                        }

                        if (Element.type === 'columns' && Element.data && Element.data.columns) {

                            for (let Column of Element.data.columns) {

                                // parse editor data for columns to avoid nesting error
                                if (typeof Column.editorData === 'string') {
                                    Column.editorData = JSON.parse(Column.editorData);
                                }

                                for (let ColumnElement of Column.editorData.blocks) {

                                    if (ColumnElement.type === 'image' && ColumnElement.data.file.hasOwnProperty('url')) {
                                        delete ColumnElement.data.file.url;
                                    }

                                    if (ColumnElement.type === 'reusableImage' && ColumnElement.data.file.hasOwnProperty('url')) {
                                        delete ColumnElement.data.file.url;
                                    }

                                }

                                // serialize editor data for columns to avoid nesting error
                                if (typeof Column.editorData !== 'string') {
                                    Column.editorData = JSON.stringify(Column.editorData);
                                }

                            }
                        }


                        if (StructureItem.SourceJourneyID !== JourneyID) {

                            if (Element.type === 'image') {
                                let copyObject = {
                                    source_key: `uploads/${TenantID}/journey/${StructureItem.SourceJourneyID}/assets/${Element.data.file.name}`,
                                    folder_name: `journey/${JourneyID}/assets`,
                                    file_name: Element.data.file.name,
                                }
                                droppedDropzoneElements.push(copyObject);
                            }

                            if (Element.type === 'reusableImage') {
                                let copyObject = {
                                    source_key: `uploads/${TenantID}/journey/${StructureItem.SourceJourneyID}/assets/${Element.data.file.name}`,
                                    folder_name: `journey/${JourneyID}/assets`,
                                    file_name: Element.data.file.name,
                                }
                                droppedDropzoneElements.push(copyObject);
                            }

                            if (Element.type === 'columns' && Element.data && Element.data.columns) {

                                for (let Column of Element.data.columns) {

                                    // parse editor data for columns to avoid nesting error
                                    if (typeof Column.editorData === 'string') {
                                        Column.editorData = JSON.parse(Column.editorData);
                                    }

                                    for (let ColumnElement of Column.editorData.blocks) {

                                        if (ColumnElement.type === 'image') {
                                            let copyObject = {
                                                source_key: `uploads/${TenantID}/journey/${StructureItem.SourceJourneyID}/assets/${ColumnElement.data.file.name}`,
                                                folder_name: `journey/${JourneyID}/assets`,
                                                file_name: ColumnElement.data.file.name,
                                            }
                                            droppedDropzoneElements.push(copyObject);
                                        }

                                        if (ColumnElement.type === 'reusableImage') {
                                            let copyObject = {
                                                source_key: `uploads/${TenantID}/journey/${StructureItem.SourceJourneyID}/assets/${ColumnElement.data.file.name}`,
                                                folder_name: `journey/${JourneyID}/assets`,
                                                file_name: ColumnElement.data.file.name,
                                            }
                                            droppedDropzoneElements.push(copyObject);
                                        }

                                    }

                                    // serialize editor data for columns to avoid nesting error
                                    if (typeof Column.editorData !== 'string') {
                                        Column.editorData = JSON.stringify(Column.editorData);
                                    }

                                }
                            }

                        }

                    }

                });
            }

            if (StructureItem.hasOwnProperty('Assignment')) {

                if (StructureItem.Assignment.hasOwnProperty('ID') && StructureItem.Assignment.ID !== null) {

                    // // save images and reusable images from checklist task
                    // if (StructureItem.Assignment.Type === 'Checklist') {
                    //
                    //     StructureItem.Assignment.Content.Tasks.forEach((Task) => {
                    //
                    //          if (Task.hasOwnProperty('Content') && Task.Content.hasOwnProperty('Elements') && Task.Content.Elements.length > 0) {
                    //
                    //              Task.Content.Elements.forEach((TaskElement) => {
                    //
                    //                  if (!StructureItem.hasOwnProperty('SourceJourneyID')) {
                    //
                    //                      if (TaskElement.type === 'image' && TaskElement.data.file.hasOwnProperty('isNew')) {
                    //
                    //                          let imageObject = {
                    //                              element_type: 'image',
                    //                              name: TaskElement.data.file.name,
                    //                              ext: TaskElement.data.file.ext,
                    //                              type: TaskElement.data.file.type,
                    //                              file: TaskElement.data.file.file,
                    //                              folder_name: `journey/${JourneyID}/assets`,
                    //                          }
                    //
                    //                          const imageName = `${TaskElement.data.file.name}.${TaskElement.data.file.ext}`;
                    //
                    //                          TaskElement.data.file = {
                    //                              name: imageName
                    //                          }
                    //                          contentNewImages.push(imageObject);
                    //                      }
                    //
                    //                      if (TaskElement.type === 'image' && !TaskElement.data.file.hasOwnProperty('isNew') && TaskElement.data.file.hasOwnProperty('url')) {
                    //                          delete TaskElement.data.file.url;
                    //                      }
                    //
                    //                      if (TaskElement.type === 'reusableImage' && TaskElement.data.file.hasOwnProperty('isNew')) {
                    //
                    //                          let reusableImageObject = {
                    //                              element_type: 'reusableImage',
                    //                              source_key: TaskElement.data.file.source_key,
                    //                              file_name: TaskElement.data.file.name,
                    //                              folder_name: `journey/${JourneyID}/assets`,
                    //                          }
                    //
                    //                          TaskElement.data.file = {
                    //                              name: reusableImageObject.file_name
                    //                          }
                    //                          contentNewImages.push(reusableImageObject);
                    //                      }
                    //
                    //                      if (TaskElement.type === 'reusableImage' && !TaskElement.data.file.hasOwnProperty('isNew') && TaskElement.data.file.hasOwnProperty('url')) {
                    //                          delete TaskElement.data.file.url;
                    //                      }
                    //
                    //                  }
                    //                  else {
                    //
                    //                      if (TaskElement.type === 'image' && TaskElement.data.file.hasOwnProperty('url')) {
                    //                          delete TaskElement.data.file.url;
                    //                      }
                    //
                    //                      if (TaskElement.type === 'reusableImage' && TaskElement.data.file.hasOwnProperty('url')) {
                    //                          delete TaskElement.data.file.url;
                    //                      }
                    //
                    //                      if (StructureItem.SourceJourneyID !== JourneyID) {
                    //
                    //                          if (TaskElement.type === 'image') {
                    //                              let copyObject = {
                    //                                  source_key: `uploads/${TenantID}/journey/${StructureItem.SourceJourneyID}/assets/${TaskElement.data.file.name}`,
                    //                                  folder_name: `journey/${JourneyID}/assets`,
                    //                                  file_name: TaskElement.data.file.name,
                    //                              }
                    //                              droppedDropzoneElements.push(copyObject);
                    //                          }
                    //
                    //                          if (TaskElement.type === 'reusableImage') {
                    //                              let copyObject = {
                    //                                  source_key: `uploads/${TenantID}/journey/${StructureItem.SourceJourneyID}/assets/${TaskElement.data.file.name}`,
                    //                                  folder_name: `journey/${JourneyID}/assets`,
                    //                                  file_name: TaskElement.data.file.name,
                    //                              }
                    //                              droppedDropzoneElements.push(copyObject);
                    //                          }
                    //
                    //                      }
                    //                  }
                    //              });
                    //         }
                    //     });
                    //
                    // }

                    if (StructureItem.Assignment.hasOwnProperty('Quizzes') && StructureItem.Assignment.Quizzes.length > 0) {

                        StructureItem.Assignment.Quizzes.forEach((quiz) => {

                            if (quiz.Type === 'Checklist') {

                                if (quiz.Content.IsDynamicDueDate === true) {
                                    let nextMeetingFrom = null;
                                    for (let j = structureIndex + 1; j < Structure.length; j++) {
                                        const nextItem = Structure[j];
                                        if (nextItem.Type === 'Event' && nextItem.Options?.IsMeeting && nextItem.Periods?.From) {
                                            nextMeetingFrom = nextItem.Periods.From;
                                            break;
                                        }
                                    }
                                    if (nextMeetingFrom) {
                                        quiz.Content.DueDate = new Date(nextMeetingFrom).toISOString();
                                        const days = Number(quiz.Content.DaysBeforeDynamicDueDate);
                                        if (!isNaN(days) && days > 0) {
                                            const reminderDate = new Date(nextMeetingFrom);
                                            reminderDate.setDate(reminderDate.getDate() - days);
                                            quiz.Content.ReminderDate = reminderDate.toISOString();
                                        } else {
                                            quiz.Content.ReminderDate = null;
                                        }
                                    } else {
                                        quiz.Content.DueDate = null;
                                        quiz.Content.ReminderDate = null;
                                    }
                                }

                                if (quiz.Content.ReminderDate !== undefined && quiz.Content.ReminderDate !== null && quiz.Content.ReminderDate !== '') {
                                    quiz.Content.ReminderDate = new Date(`${quiz.Content.ReminderDate}`).toISOString();
                                }
                                if (quiz.Content.DueDate !== undefined && quiz.Content.DueDate !== null && quiz.Content.DueDate !== '') {
                                    quiz.Content.DueDate = new Date(`${quiz.Content.DueDate}`).toISOString();
                                }

                                if (quiz.Content.hasOwnProperty('Tasks') && quiz.Content.Tasks.length > 0) {
                                    quiz.Content.Tasks.forEach((task) => {
                                        if (task.hasOwnProperty('Content') && task.Content.hasOwnProperty('Elements') && task.Content.Elements.length > 0) {

                                            task.Content.Elements.forEach((Element) => {

                                                if (!StructureItem.hasOwnProperty('SourceJourneyID')) {
                                                    if (Element.type === 'image' && Element.data.file.hasOwnProperty('isNew')) {

                                                        let imageObject = {
                                                            element_type: 'image',
                                                            name: Element.data.file.name,
                                                            ext: Element.data.file.ext,
                                                            type: Element.data.file.type,
                                                            file: Element.data.file.file,
                                                            folder_name: `journey/${JourneyID}/assets`,
                                                        }

                                                        const imageName = `${Element.data.file.name}.${Element.data.file.ext}`;

                                                        Element.data.file = {
                                                            name: imageName
                                                        }
                                                        contentNewImages.push(imageObject);
                                                    }

                                                    if (Element.type === 'image' && !Element.data.file.hasOwnProperty('isNew') && Element.data.file.hasOwnProperty('url')) {
                                                        delete Element.data.file.url;
                                                    }

                                                    if (Element.type === 'reusableImage' && Element.data.file.hasOwnProperty('isNew')) {

                                                        let reusableImageObject = {
                                                            element_type: 'reusableImage',
                                                            source_key: Element.data.file.source_key,
                                                            file_name: Element.data.file.name,
                                                            folder_name: `journey/${JourneyID}/assets`,
                                                        }

                                                        Element.data.file = {
                                                            name: reusableImageObject.file_name
                                                        }
                                                        contentNewImages.push(reusableImageObject);
                                                    }

                                                    if (Element.type === 'reusableImage' && !Element.data.file.hasOwnProperty('isNew') && Element.data.file.hasOwnProperty('url')) {
                                                        delete Element.data.file.url;
                                                    }

                                                    if (Element.type === 'columns' && Element.data && Element.data.columns) {

                                                        for (let Column of Element.data.columns) {

                                                            // parse editor data for columns to avoid nesting error
                                                            if (typeof Column.editorData === 'string') {
                                                                Column.editorData = JSON.parse(Column.editorData);
                                                            }

                                                            for (let ColumnElement of Column.editorData.blocks) {

                                                                if (ColumnElement.type === 'image' && ColumnElement.data.file.hasOwnProperty('isNew')) {

                                                                    let imageObject = {
                                                                        element_type: 'image',
                                                                        name: ColumnElement.data.file.name,
                                                                        ext: ColumnElement.data.file.ext,
                                                                        type: ColumnElement.data.file.type,
                                                                        file: ColumnElement.data.file.file,
                                                                        folder_name: `journey/${JourneyID}/assets`,
                                                                    }

                                                                    const imageName = `${ColumnElement.data.file.name}.${ColumnElement.data.file.ext}`;

                                                                    ColumnElement.data.file = {
                                                                        name: imageName
                                                                    }
                                                                    contentNewImages.push(imageObject);
                                                                }

                                                                if (ColumnElement.type === 'image' && !ColumnElement.data.file.hasOwnProperty('isNew') && ColumnElement.data.file.hasOwnProperty('url')) {
                                                                    delete ColumnElement.data.file.url;
                                                                }

                                                                if (ColumnElement.type === 'reusableImage' && ColumnElement.data.file.hasOwnProperty('isNew')) {

                                                                    let reusableImageObject = {
                                                                        element_type: 'reusableImage',
                                                                        source_key: ColumnElement.data.file.source_key,
                                                                        file_name: ColumnElement.data.file.name,
                                                                        folder_name: `journey/${JourneyID}/assets`,
                                                                    }

                                                                    ColumnElement.data.file = {
                                                                        name: reusableImageObject.file_name
                                                                    }
                                                                    contentNewImages.push(reusableImageObject);
                                                                }

                                                                if (ColumnElement.type === 'reusableImage' && !ColumnElement.data.file.hasOwnProperty('isNew') && ColumnElement.data.file.hasOwnProperty('url')) {
                                                                    delete ColumnElement.data.file.url;
                                                                }

                                                            }

                                                            // serialize editor data for columns to avoid nesting error
                                                            if (typeof Column.editorData !== 'string') {
                                                                Column.editorData = JSON.stringify(Column.editorData);
                                                            }

                                                        }
                                                    }

                                                }

                                                else {

                                                    if (Element.type === 'image' && Element.data.file.hasOwnProperty('url')) {
                                                        delete Element.data.file.url;
                                                    }

                                                    if (Element.type === 'reusableImage' && Element.data.file.hasOwnProperty('url')) {
                                                        delete Element.data.file.url;
                                                    }

                                                    if (Element.type === 'columns' && Element.data && Element.data.columns) {

                                                        for (let Column of Element.data.columns) {

                                                            // parse editor data for columns to avoid nesting error
                                                            if (typeof Column.editorData === 'string') {
                                                                Column.editorData = JSON.parse(Column.editorData);
                                                            }

                                                            for (let ColumnElement of Column.editorData.blocks) {

                                                                if (ColumnElement.type === 'image' && ColumnElement.data.file.hasOwnProperty('url')) {
                                                                    delete ColumnElement.data.file.url;
                                                                }

                                                                if (ColumnElement.type === 'reusableImage' && ColumnElement.data.file.hasOwnProperty('url')) {
                                                                    delete ColumnElement.data.file.url;
                                                                }

                                                            }

                                                            // serialize editor data for columns to avoid nesting error
                                                            if (typeof Column.editorData !== 'string') {
                                                                Column.editorData = JSON.stringify(Column.editorData);
                                                            }

                                                        }
                                                    }


                                                    if (StructureItem.SourceJourneyID !== JourneyID) {

                                                        if (Element.type === 'image') {
                                                            let copyObject = {
                                                                source_key: `uploads/${TenantID}/journey/${StructureItem.SourceJourneyID}/assets/${Element.data.file.name}`,
                                                                folder_name: `journey/${JourneyID}/assets`,
                                                                file_name: Element.data.file.name,
                                                            }
                                                            droppedDropzoneElements.push(copyObject);
                                                        }

                                                        if (Element.type === 'reusableImage') {
                                                            let copyObject = {
                                                                source_key: `uploads/${TenantID}/journey/${StructureItem.SourceJourneyID}/assets/${Element.data.file.name}`,
                                                                folder_name: `journey/${JourneyID}/assets`,
                                                                file_name: Element.data.file.name,
                                                            }
                                                            droppedDropzoneElements.push(copyObject);
                                                        }

                                                        if (Element.type === 'columns' && Element.data && Element.data.columns) {

                                                            for (let Column of Element.data.columns) {

                                                                // parse editor data for columns to avoid nesting error
                                                                if (typeof Column.editorData === 'string') {
                                                                    Column.editorData = JSON.parse(Column.editorData);
                                                                }

                                                                for (let ColumnElement of Column.editorData.blocks) {

                                                                    if (ColumnElement.type === 'image') {
                                                                        let copyObject = {
                                                                            source_key: `uploads/${TenantID}/journey/${StructureItem.SourceJourneyID}/assets/${ColumnElement.data.file.name}`,
                                                                            folder_name: `journey/${JourneyID}/assets`,
                                                                            file_name: ColumnElement.data.file.name,
                                                                        }
                                                                        droppedDropzoneElements.push(copyObject);
                                                                    }

                                                                    if (ColumnElement.type === 'reusableImage') {
                                                                        let copyObject = {
                                                                            source_key: `uploads/${TenantID}/journey/${StructureItem.SourceJourneyID}/assets/${ColumnElement.data.file.name}`,
                                                                            folder_name: `journey/${JourneyID}/assets`,
                                                                            file_name: ColumnElement.data.file.name,
                                                                        }
                                                                        droppedDropzoneElements.push(copyObject);
                                                                    }

                                                                }

                                                                // serialize editor data for columns to avoid nesting error
                                                                if (typeof Column.editorData !== 'string') {
                                                                    Column.editorData = JSON.stringify(Column.editorData);
                                                                }

                                                            }
                                                        }

                                                    }

                                                }

                                            });

                                        }
                                    });
                                }

                            }

                            if (quiz.Type === 'Announcement') {

                                if (quiz.Content.ReminderDate !== undefined && quiz.Content.ReminderDate !== null && quiz.Content.ReminderDate !== '') {
                                    quiz.Content.ReminderDate = new Date(`${quiz.Content.ReminderDate}`).toISOString();
                                }

                            }
                        });

                        // mark Assignments that only have Announcement quizzes to be skipped
                        let quizzesWithoutAnnouncements = StructureItem.Assignment.Quizzes.filter((quiz) => quiz.Type !== 'Announcement');
                        StructureItem.ExcludeJourneyCompletionCalculation = (quizzesWithoutAnnouncements.length < 1);
                    }
                    else {
                        // If no quizzes, exclude from journey completion calculation
                        StructureItem.ExcludeJourneyCompletionCalculation = true;
                    }

                }

            }

            delete StructureItem.SourceJourneyID;

        }

    });

    return {
        contentNewImages: contentNewImages,
        journeyLinkedAssets: journeyLinkedAssets,
        droppedDropzoneElements: droppedDropzoneElements,
    };
};

const checkOverviewForNewImages = async function(JourneyID, Overview) {

    let overviewNewImages = [];

    Overview.Elements.forEach((Element) => {

        if (Element.type === 'image' && Element.data.file.hasOwnProperty('isNew')) {

            let imageObject = {
                element_type: 'image',
                name: Element.data.file.name,
                ext: Element.data.file.ext,
                type: Element.data.file.type,
                file: Element.data.file.file,
                folder_name: `journey/${JourneyID}/assets`,
            }

            const imageName = `${Element.data.file.name}.${Element.data.file.ext}`;

            Element.data.file = {
                name: imageName
            }
            overviewNewImages.push(imageObject);
        }

        if (Element.type === 'image' && !Element.data.file.hasOwnProperty('isNew') && Element.data.file.hasOwnProperty('url')) {
            delete Element.data.file.url;
        }

        if (Element.type === 'reusableImage' && Element.data.file.hasOwnProperty('isNew')) {

            let reusableImageObject = {
                element_type: 'reusableImage',
                source_key: Element.data.file.source_key,
                file_name: Element.data.file.name,
                folder_name: `journey/${JourneyID}/assets`,
            }

            Element.data.file = {
                name: reusableImageObject.file_name
            }
            overviewNewImages.push(reusableImageObject);
        }

        if (Element.type === 'reusableImage' && !Element.data.file.hasOwnProperty('isNew') && Element.data.file.hasOwnProperty('url')) {
            delete Element.data.file.url;
        }

        if (Element.type === 'columns' && Element.data && Element.data.columns) {

            for (let Column of Element.data.columns) {

                // parse editor data for columns to avoid nesting error
                if (typeof Column.editorData === 'string') {
                    Column.editorData = JSON.parse(Column.editorData);
                }

                for (let ColumnElement of Column.editorData.blocks) {

                    if (ColumnElement.type === 'image' && ColumnElement.data.file.hasOwnProperty('isNew')) {

                        let imageObject = {
                            element_type: 'image',
                            name: ColumnElement.data.file.name,
                            ext: ColumnElement.data.file.ext,
                            type: ColumnElement.data.file.type,
                            file: ColumnElement.data.file.file,
                            folder_name: `journey/${JourneyID}/assets`,
                        }

                        const imageName = `${ColumnElement.data.file.name}.${ColumnElement.data.file.ext}`;

                        ColumnElement.data.file = {
                            name: imageName
                        }
                        overviewNewImages.push(imageObject);
                    }

                    if (ColumnElement.type === 'image' && !ColumnElement.data.file.hasOwnProperty('isNew') && ColumnElement.data.file.hasOwnProperty('url')) {
                        delete ColumnElement.data.file.url;
                    }

                    if (ColumnElement.type === 'reusableImage' && ColumnElement.data.file.hasOwnProperty('isNew')) {

                        let reusableImageObject = {
                            element_type: 'reusableImage',
                            source_key: ColumnElement.data.file.source_key,
                            file_name: ColumnElement.data.file.name,
                            folder_name: `journey/${JourneyID}/assets`,
                        }

                        ColumnElement.data.file = {
                            name: reusableImageObject.file_name
                        }
                        overviewNewImages.push(reusableImageObject);
                    }

                    if (ColumnElement.type === 'reusableImage' && !ColumnElement.data.file.hasOwnProperty('isNew') && ColumnElement.data.file.hasOwnProperty('url')) {
                        delete ColumnElement.data.file.url;
                    }

                }

                // serialize editor data for columns to avoid nesting error
                if (typeof Column.editorData !== 'string') {
                    Column.editorData = JSON.stringify(Column.editorData);
                }

            }
        }
    });

    return overviewNewImages;

};

const state = getDefaultState();

const getters = {
    getUserJourneys: state => state.userJourneys,
    getLastUserJourney: state => state.lastUserJourney,
    getUserAuthorJourneys: state => {
        return state.userJourneys.filter((userJourney) => userJourney.IsCurrentUserAuthor);
    },
    getLastUpdated: state => state.lastUpdated,
    getIsParticipantView: state => state.isParticipantView,
};

const actions = {
    // clearLastOpenedFullJourney({ commit }) {
    //     commit('CLEAR_LAST_OPENED_FULL_JOURNEY');
    // },

    async list({ commit, dispatch, rootState}) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            };
            let payload = {

            };

            const response = await axios.post('/v2/journey/list', payload, config);

            return response.data.journeysList;
        }
        catch (err) {
            // throw new Error(err);
        }
    },

    async listUserJourneys({ commit, dispatch, state, rootState }, isDashboard = false) {
        // If offline and we have cached data, return it
        if (!navigator.onLine && state.userJourneys.length > 0) {
            console.log('Offline: Using cached journeys');
            return state.userJourneys;
        }

      const fetchFromApi = async () => {
        commit('SET_LAST_UPDATED', null);

        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
        const config = {
          headers: {
            Authorization: rootState.auth.bearer,
          },
        };

        const payloadAPI = {};

        const response = await axios.post('/v2/journey/list-user-journeys', payloadAPI, config);

        let userJourneyList = mergeJourneyListPreservingStructure(
          response.data.userJourneyList,
          state.userJourneys,
        );

        commit('SET_USER_JOURNEYS', userJourneyList);
        commit('SET_LAST_UPDATED', new Date());

        if (isDashboard) {
          const participantRows = userJourneyList.filter((journey) => journey.IsCurrentUserParticipant);
          const journeyIDs = participantRows
            .filter((row) => !hasUsableDashboardStructure(row))
            .map((journey) => journey.ID);

          const limitedFetches = journeyIDs.map((id) =>
            limit(() => dispatch('getFullJourney', {
              JourneyID: id,
            })),
          );

          let detailedJourneys = limitedFetches.length ? await Promise.all(limitedFetches) : [];
          detailedJourneys = detailedJourneys.filter(j => j !== null);

          if (detailedJourneys.length > 0) {
            userJourneyList = userJourneyList.map(summaryJourney => {
              const detailed = detailedJourneys.find(j => j.ID === summaryJourney.ID);
              return detailed ? detailed : summaryJourney;
            });

            commit('SET_USER_JOURNEYS', userJourneyList);
          }
        }

        return userJourneyList;
      };

      const hasBearer = !!rootState.auth?.bearer;
      const hasCachedList = state.userJourneys.length > 0;

      if (process.env.NODE_ENV === 'development') {
        console.debug('[journey/listUserJourneys]', {
          branch: navigator.onLine && hasCachedList && hasBearer ? 'fast-path' : 'await-api',
          hasCachedList,
          hasBearer,
          isDashboard: !!isDashboard,
        });
      }

      if (navigator.onLine && hasCachedList && hasBearer) {
        fetchFromApi().catch((err) => {
          if (!navigator.onLine && state.userJourneys.length > 0) {
            console.log('Offline during background journey refresh, keeping cache');
            return;
          }
          console.error('Background refresh user journeys failed:', err);
        });
        return state.userJourneys;
      }

      try {
        return await fetchFromApi();
      } catch (err) {
        if (!navigator.onLine && state.userJourneys.length > 0) {
          console.log('Offline: Fetch failed, using cached journeys');
          return state.userJourneys;
        }
        console.error('Error fetching user journeys:', err);
        throw err;
      }
    },

    // async getFullJourney({ commit, state, rootState }, payload) {
    //     const journeyID = payload.JourneyID;
    //     const currentTenantId = rootState.tenant?.tenantinfo?.id ?? null;
    //
    //     const lastOpenedMatchesTenant = () => {
    //         const lo = state.lastOpenedFullJourney;
    //         return (
    //             lo.tenantId != null &&
    //             currentTenantId != null &&
    //             String(lo.tenantId) === String(currentTenantId)
    //         );
    //     };
    //
    //     const listEntryMatchesTenant = (j) => {
    //         if (!j || !j.Structure) return false;
    //         if (j.ID !== journeyID && String(j.ID) !== String(journeyID)) return false;
    //         if (j.TenantID == null || currentTenantId == null) return true;
    //         return String(j.TenantID) === String(currentTenantId);
    //     };
    //
    //     const getUsableCachedJourney = () => {
    //         if (
    //             lastOpenedMatchesTenant() &&
    //             (state.lastOpenedFullJourney.journeyId === journeyID ||
    //                 String(state.lastOpenedFullJourney.journeyId) === String(journeyID)) &&
    //             state.lastOpenedFullJourney.journey &&
    //             state.lastOpenedFullJourney.journey.Structure
    //         ) {
    //             return state.lastOpenedFullJourney.journey;
    //         }
    //         const fromList = state.userJourneys.find(
    //             (journey) =>
    //                 (journey.ID === journeyID || String(journey.ID) === String(journeyID)) &&
    //                 journey.Structure,
    //         );
    //         if (fromList && listEntryMatchesTenant(fromList)) {
    //             return fromList;
    //         }
    //         return null;
    //     };
    //
    //     if (!navigator.onLine) {
    //         const cached = getUsableCachedJourney();
    //         if (cached) {
    //             console.log('Offline: Using cached full journey');
    //             return cached;
    //         }
    //         const cachedJourney = state.userJourneys.find(
    //             (journey) => journey.ID === journeyID || String(journey.ID) === String(journeyID),
    //         );
    //         if (cachedJourney && cachedJourney.Structure) {
    //             console.log('Offline: Using cached full journey from list');
    //             return cachedJourney;
    //         }
    //     }
    //
    //     const fetchFromApi = async () => {
    //         axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    //         const config = {
    //             headers: {
    //                 Authorization: rootState.auth.bearer,
    //             },
    //         };
    //
    //         const payloadAPI = {
    //             JourneyID: payload.JourneyID,
    //         };
    //
    //         const response = await axios.post('/v2/journey/fetch-journey', payloadAPI, config);
    //
    //         const journey = response.data.journey;
    //         commit('SET_USER_JOURNEY', journey);
    //         commit('SET_LAST_OPENED_FULL_JOURNEY', {
    //             journeyId: journey.ID,
    //             journey,
    //             tenantId: rootState.tenant?.tenantinfo?.id ?? null,
    //             cachedAt: new Date().toISOString(),
    //         });
    //
    //         return journey;
    //     };
    //
    //     const hasBearer = !!rootState.auth?.bearer;
    //
    //     if (navigator.onLine && hasBearer) {
    //         const usable = getUsableCachedJourney();
    //         if (usable) {
    //             const clone = JSON.parse(JSON.stringify(usable));
    //             commit('SET_USER_JOURNEY', clone);
    //             fetchFromApi().catch((err) => {
    //                 if (!navigator.onLine) {
    //                     console.log('Offline during journey refresh, keeping cache');
    //                     return;
    //                 }
    //                 console.error('Background refresh full journey failed:', err);
    //             });
    //             return clone;
    //         }
    //     }
    //
    //     try {
    //         return await fetchFromApi();
    //     } catch (err) {
    //         if (!navigator.onLine) {
    //             const cachedJourney = state.userJourneys.find(
    //                 (journey) => journey.ID === journeyID || String(journey.ID) === String(journeyID),
    //             );
    //             if (cachedJourney) {
    //                 console.log('Offline: Fetch failed, using cached journey');
    //                 return cachedJourney;
    //             }
    //         }
    //         console.error('Error fetching user journeys:', err);
    //         throw err;
    //     }
    // },

    async getFullJourney({ commit, dispatch, state, rootState }, payload) {
        // If offline, try to find the journey in cached userJourneys
        if (!navigator.onLine) {
            const cachedJourney = state.userJourneys.find((journey) => journey.ID === payload.JourneyID);
            if (cachedJourney && cachedJourney.Structure) {
                console.log('Offline: Using cached full journey');
                return cachedJourney;
            }
        }

        try {

            axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
            const config = {
                headers: {
                    Authorization: rootState.auth.bearer,
                },
            };

            // let currentDateTimeUserTimezone = convertIsoStringToLocalDateTime(new Date());

            const payloadAPI = {
                JourneyID: payload.JourneyID,
                // CurrentDateTimeUserTimezone: currentDateTimeUserTimezone,
            }

            const response = await axios.post('/v2/journey/fetch-journey', payloadAPI, config);

            commit('SET_USER_JOURNEY', response.data.journey);

            return response.data.journey;

        } catch (err) {
            // If offline and fetch failed, try to return cached journey
            if (!navigator.onLine) {
                const cachedJourney = state.userJourneys.find((journey) => journey.ID === payload.JourneyID);
                if (cachedJourney) {
                    console.log('Offline: Fetch failed, using cached journey');
                    return cachedJourney;
                }
            }
            console.error('Error fetching user journeys:', err);
            throw err;
        }
    },

    async listAllJourneysNames({ commit, dispatch, rootState}) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            };
            let payload = {

            };

            const response = await axios.post('/v2/journey/list-all-journeys-names', payload, config);

            return response.data.journeysNames;

        }
        catch (err) {
            // throw new Error(err);
        }
    },

    async create({ commit, dispatch, rootState }, newJourney) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            };

            let journeyPayload = {
                "Name": newJourney.Name,
                "Description": newJourney.Description,
                "CategoryID": newJourney.CategoryID,
                "Language": newJourney.Language,
                "Picture": null,
                "Overview": newJourney.Overview,
                "AuthorNotes": newJourney.AuthorNotes || { Time: null, Elements: [], Version: null },
                "Structure": newJourney.Structure
            }

            let isPictureUpdated = (newJourney.hasOwnProperty('PictureUpdated'));

            if (isPictureUpdated) {
                journeyPayload.Picture =`${newJourney.PictureUpdated.name}.${newJourney.PictureUpdated.ext}`;
            }

            // save overview images to S3
            let overviewNewImages = await checkOverviewForNewImages(null, newJourney.Overview);
            let authorNotesNewImages = await checkOverviewForNewImages(null, journeyPayload.AuthorNotes);

            // // need to call it before so the base 64 data gets deleted from payload object
            // let contentNewImages = await checkChaptersForNewImages(null, newJourney.Chapters);

            const response = await axios.post('/v2/journey/create', journeyPayload, config);

            let newCreatedJourney = response.data.newJourney;

            if (overviewNewImages.length > 0) {
                for (const overviewNewImage of overviewNewImages) {
                    overviewNewImage.folder_name = `journey/${newCreatedJourney.ID}/assets`;
                    if (overviewNewImage.element_type === 'image') {
                        await dispatch('file/uploadFile', overviewNewImage, {root: true});
                    }
                    if (overviewNewImage.element_type === 'reusableImage') {
                        await dispatch('file/copyFileBetweenS3Folders', overviewNewImage, {root: true});
                    }
                }
            }

            if (authorNotesNewImages.length > 0) {
                for (const authorNotesNewImage of authorNotesNewImages) {
                    authorNotesNewImage.folder_name = `journey/${newCreatedJourney.ID}/assets`;
                    if (authorNotesNewImage.element_type === 'image') {
                        await dispatch('file/uploadFile', authorNotesNewImage, {root: true});
                    }
                    if (authorNotesNewImage.element_type === 'reusableImage') {
                        await dispatch('file/copyFileBetweenS3Folders', authorNotesNewImage, {root: true});
                    }
                }
            }

            // // save blocks images to S3, after save was finished, now we have a Journey ID, so we need to add it to the S3 folder path
            // if (contentNewImages.length > 0) {
            //     for (const contentNewImage of contentNewImages) {
            //         contentNewImage.folder_name = `journey/${newCreatedJourney.ID}/assets`;
            //         await dispatch('file/uploadFile', contentNewImage, {root: true});
            //     }
            // }

            if (isPictureUpdated) {
                let journeyPicturePayload = newJourney.PictureUpdated;
                journeyPicturePayload.folder_name = `journey/${newCreatedJourney.ID}`;
                await dispatch('file/uploadFile', journeyPicturePayload, {root: true});
                newCreatedJourney.PictureURL = await dispatch('file/getFile', {
                    file_name: newCreatedJourney.Picture,
                    folder_name: `journey/${newCreatedJourney.ID}`
                }, {root: true});
            }

            newCreatedJourney.IsCurrentUserAuthor = true;
            newCreatedJourney.IsCurrentUserParticipant = false;

            commit('ADD_JOURNEY', newCreatedJourney);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Journey successfully created'
            }, {root: true});

            return newCreatedJourney;

        }
        catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: 'Journey failed to be created'
            }, {root: true});
            throw new Error(err);
        }
    },

    async copyJourney({ commit, dispatch, rootState }, payload) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            commit('loading/setLoading', true, { root: true });

            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            };

            const response = await axios.post('/v2/journey/copy-journey', payload, config);

            let newCreatedJourney = response.data.newJourney;

            // newCreatedJourney.IsCurrentUserAuthor = true;
            // newCreatedJourney.IsCurrentUserParticipant = false;

            // commit('ADD_JOURNEY', newCreatedJourney);

            commit('loading/setLoading', false, { root: true });

            // files referenced in the source journey that couldn't be copied (e.g. missing from S3), the copy still went through
            const missingAssets = response.data.missingAssets || [];

            if (missingAssets.length > 0) {
                const maxDisplayedNames = 5;
                const missingAssetsNames = missingAssets.map((missingAsset) => missingAsset.Label || missingAsset.Name);
                let names = missingAssetsNames.slice(0, maxDisplayedNames).join(', ');
                if (missingAssetsNames.length > maxDisplayedNames) {
                    names += ` (+${missingAssetsNames.length - maxDisplayedNames})`;
                }

                dispatch('notifications/addNotification', {
                    type: 'warning',
                    messageKey: 'notifications.journeyCopiedWithMissingAssets',
                    messageParams: { names },
                    duration: 12000,
                }, {root: true});
            }
            else {
                dispatch('notifications/addNotification', {
                    type: 'success',
                    message: 'Journey successfully created'
                }, {root: true});
            }

            return newCreatedJourney;

        }
        catch (err) {
            commit('loading/setLoading', false, { root: true });

            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else if (err.response?.data?.notAllowedForTemplate) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedForTemplate'
                }, {root: true});
            }
            else if (!err.response?.data?.expiredAccount) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Journey failed to be created'
                }, {root: true});
            }
            throw err;
        }
    },

    async update({ commit, dispatch, rootState }, updatedJourney) {
        commit('loading/setLoading', true, { root: true });

        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {

            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            // save overview images to S3
            let overviewNewImages = await checkOverviewForNewImages(updatedJourney.ID, updatedJourney.Overview);
            let authorNotesNewImages = await checkOverviewForNewImages(
                updatedJourney.ID,
                updatedJourney.AuthorNotes || { Elements: [] }
            );

            if (overviewNewImages.length > 0) {
                for (const overviewNewImage of overviewNewImages) {
                    if (overviewNewImage.element_type === 'image') {
                        await dispatch('file/uploadFile', overviewNewImage, {root: true});
                    }
                    if (overviewNewImage.element_type === 'reusableImage') {
                        await dispatch('file/copyFileBetweenS3Folders', overviewNewImage, {root: true});
                    }
                }
            }

            if (authorNotesNewImages.length > 0) {
                for (const authorNotesNewImage of authorNotesNewImages) {
                    if (authorNotesNewImage.element_type === 'image') {
                        await dispatch('file/uploadFile', authorNotesNewImage, {root: true});
                    }
                    if (authorNotesNewImage.element_type === 'reusableImage') {
                        await dispatch('file/copyFileBetweenS3Folders', authorNotesNewImage, {root: true});
                    }
                }
            }

            // save blocks images to S3
            let {contentNewImages, journeyLinkedAssets, droppedDropzoneElements} = await prepareJourneyStructureForSave(updatedJourney.ID, updatedJourney.Structure, updatedJourney.TenantID);

            if (contentNewImages.length > 0) {
                for (const contentNewImage of contentNewImages) {
                    if (contentNewImage.element_type === 'image') {
                        await dispatch('file/uploadFile', contentNewImage, {root: true});
                    }
                    if (contentNewImage.element_type === 'reusableImage') {
                        await dispatch('file/copyFileBetweenS3Folders', contentNewImage, {root: true});
                    }
                }
            }

            if (journeyLinkedAssets.length > 0) {
                for (const unitAsset of journeyLinkedAssets) {
                    await dispatch('file/uploadFile', unitAsset, {root: true});
                }
            }

            if (droppedDropzoneElements.length > 0) {
                for (const droppedDropzoneElement of droppedDropzoneElements) {
                    await dispatch('file/copyFileBetweenS3Folders', droppedDropzoneElement, {root: true});
                }
            }

            let journeyPayload = {
                "AuthorID": updatedJourney.AuthorID,
                "Name": updatedJourney.Name,
                "Description": updatedJourney.Description,
                "CategoryID": updatedJourney.CategoryID,
                "Language": updatedJourney.Language,
                "Picture": updatedJourney.Picture,
                "Overview": updatedJourney.Overview,
                "AuthorNotes": updatedJourney.AuthorNotes || { Time: null, Elements: [], Version: null },
                "Structure": updatedJourney.Structure,
            };

            if (rootState.tenant.tenantinfo?.b2c === true && updatedJourney.hasOwnProperty('Certificate')) {
                journeyPayload.Certificate = updatedJourney.Certificate;
            }

            if (updatedJourney.hasOwnProperty('ExistingAssetsToDelete') && updatedJourney.ExistingAssetsToDelete.length > 0) {
                journeyPayload.ExistingAssetsToDelete = updatedJourney.ExistingAssetsToDelete;

                for (let ExistingAssetToDelete of updatedJourney.ExistingAssetsToDelete) {
                    let payloadS3 = {
                        folder_name: `journey/${updatedJourney.ID}/assets`,
                        file_name: ExistingAssetToDelete
                    }
                    await dispatch('file/deleteFile', payloadS3, {root: true});
                }
            }

            let isPictureUpdated = (updatedJourney.hasOwnProperty('PictureUpdated'));
            let picture_old = updatedJourney.Picture;

            if (isPictureUpdated) {
                picture_old = updatedJourney.Picture;
                journeyPayload.Picture =`${updatedJourney.PictureUpdated.name}.${updatedJourney.PictureUpdated.ext}`;
            }

            // save updated Journey
            const response = await axios.put(`/v2/journey/${updatedJourney.ID}`, journeyPayload, config);

            let refetchedJourney = await dispatch('getFullJourney', { JourneyID: updatedJourney.ID })

            refetchedJourney.Overview.Elements.forEach((Element) => {
                if (Element.type === 'columns' && Element.data && Element.data.columns) {
                    for (let Column of Element.data.columns) {
                        // parse editor data for columns to avoid nesting error
                        if (typeof Column.editorData === 'string') {
                            Column.editorData = JSON.parse(Column.editorData);
                        }
                    }
                }
            });

            refetchedJourney.Structure.forEach((StructureItem) => {

                if (StructureItem.Type === 'Chapter') {
                    if (StructureItem.hasOwnProperty('Units')) {
                        StructureItem.Units.forEach((Unit, indexUnit) => {
                            if (Unit.hasOwnProperty('Blocks')) {
                                Unit.Blocks.forEach((Block) => {
                                    if (Block.hasOwnProperty('Elements')) {
                                        Block.Elements.forEach((Element) => {
                                            if (Element.type === 'columns' && Element.data && Element.data.columns) {
                                                for (let Column of Element.data.columns) {
                                                    // parse editor data for columns to avoid nesting error
                                                    if (typeof Column.editorData === 'string') {
                                                        Column.editorData = JSON.parse(Column.editorData);
                                                    }
                                                }
                                            }
                                        });
                                    }
                                });
                            }
                        });
                    }
                }

                if (StructureItem.Type === 'Event') {

                    if (StructureItem.hasOwnProperty('Details') && StructureItem.Details.hasOwnProperty('Elements') && StructureItem.Details.Elements.length > 0) {
                        StructureItem.Details.Elements.forEach((Element) => {
                            if (Element.type === 'columns' && Element.data && Element.data.columns) {
                                for (let Column of Element.data.columns) {
                                    // parse editor data for columns to avoid nesting error
                                    if (typeof Column.editorData === 'string') {
                                        Column.editorData = JSON.parse(Column.editorData);
                                    }
                                }
                            }
                        });
                    }

                    if (StructureItem.hasOwnProperty('Assignment')) {

                        if (StructureItem.Assignment.hasOwnProperty('ID') && StructureItem.Assignment.ID !== null) {

                            if (StructureItem.Assignment.hasOwnProperty('Quizzes') && StructureItem.Assignment.Quizzes.length > 0) {

                                StructureItem.Assignment.Quizzes.forEach((quiz) => {

                                    if (quiz.Type === 'Checklist') {

                                        if (quiz.Content.hasOwnProperty('Tasks') && quiz.Content.Tasks.length > 0) {
                                            quiz.Content.Tasks.forEach((task) => {
                                                if (task.hasOwnProperty('Content') && task.Content.hasOwnProperty('Elements') && task.Content.Elements.length > 0) {
                                                    task.Content.Elements.forEach((Element) => {
                                                        if (Element.type === 'columns' && Element.data && Element.data.columns) {
                                                            for (let Column of Element.data.columns) {
                                                                // parse editor data for columns to avoid nesting error
                                                                if (typeof Column.editorData === 'string') {
                                                                    Column.editorData = JSON.parse(Column.editorData);
                                                                }
                                                            }
                                                        }
                                                    });
                                                }
                                            });
                                        }
                                    }
                                });
                            }

                        }

                    }
                }
            });



            // save Journey main Picture if updated
            if (isPictureUpdated) {
                let journeyPicturePayload = updatedJourney.PictureUpdated;
                journeyPicturePayload.folder_name = `journey/${updatedJourney.ID}`;
                await dispatch('file/uploadFile', journeyPicturePayload, {root: true});
                // get main Picture download pre-signed url
                refetchedJourney.PictureURL = await dispatch('file/getFile', {
                    file_name: refetchedJourney.Picture,
                    folder_name: `journey/${refetchedJourney.ID}`
                }, {root: true});

                // delete Journey old main Picture
                if (picture_old !== null) {
                    const delete_payload = {
                        folder_name: `journey/${updatedJourney.ID}`,
                        file_name: picture_old
                    }
                    await dispatch('file/deleteFile', delete_payload, {root: true});
                }
            }

            refetchedJourney.IsCurrentUserAuthor = updatedJourney.IsCurrentUserAuthor;
            refetchedJourney.IsCurrentUserParticipant = updatedJourney.IsCurrentUserParticipant;

            commit('UPDATE_JOURNEY', refetchedJourney);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Journey successfully updated'
            }, {root: true});

            commit('loading/setLoading', false, { root: true });

        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else if (!err.response?.data?.expiredAccount && !err.response?.data?.expiredLinkedUsers) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Journey failed to be updated'
                }, {root: true});
            }

            commit('loading/setLoading', false, { root: true });

            throw err;
        }
    },

    async updateActiveStatus({ commit, dispatch, rootState }, updatedJourney) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            let payload = {
                "Active": !updatedJourney.Active,
            }

            const response = await axios.put(`/v2/journey/update-active-status/${updatedJourney.ID}`, payload, config);

            commit('UPDATE_JOURNEY_ACTIVE_STATUS', response.data.updatedJourney);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Journey status successfully updated'
            }, {root: true});

        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Journey status failed to be updated'
                }, {root: true});
            }
            throw new Error(err);
        }
    },

    async updateJourneyHideFromTenantAdmin({ commit, dispatch, rootState }, updatedJourney) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            let payload = {
                "IsHideFromTenantAdmin": !updatedJourney.IsHideFromTenantAdmin,
            }

            const response = await axios.put(`/v2/journey/update-hide-from-tenant/${updatedJourney.ID}`, payload, config);

            commit('UPDATE_JOURNEY_HIDE_FROM_TENANT_ADMIN', response.data.updatedJourney);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Journey successfully hidden from tenant'
            }, {root: true});

        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Journey failed to be hidden from tenant'
                }, {root: true});
            }
            throw new Error(err);
        }
    },

    async updateJourneyAnonymousParticipantMode({ commit, rootState }, { JourneyID, IsAnonymousParticipantMode }) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        let config = {
            headers: {
                Authorization: rootState.auth.bearer
            },
        }

        let payload = {
            "IsAnonymousParticipantMode": IsAnonymousParticipantMode,
        }

        const response = await axios.put(`/v2/journey/update-anonymous-participant-mode/${JourneyID}`, payload, config);

        commit('UPDATE_JOURNEY_ANONYMOUS_PARTICIPANT_MODE', response.data.updatedJourney);
    },

    async updateAuthor({ commit, dispatch, rootState }, updatedJourney) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            let payloadAPI = {
                // "JourneyID": updatedJourney.JourneyID,
                "NewAuthorID": updatedJourney.NewAuthorID,
                "OldAuthorID": updatedJourney.OldAuthorID,
            }

            const response = await axios.put(`/v2/journey/update-author/${updatedJourney.JourneyID}`, payloadAPI, config);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Journey author successfully updated'
            }, {root: true});

            return response.data.updatedJourney;
        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Journey author failed to be updated'
                }, {root: true});
            }
            throw new Error(err);
        }
    },

    async updateFromJourneyManagement({ commit, dispatch, rootState }, updatedJourney) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            let payloadAPI = {
                // "JourneyID": updatedJourney.JourneyID,
                "NewAuthorID": updatedJourney.NewAuthorID,
                "OldAuthorID": updatedJourney.OldAuthorID,
                "Language": updatedJourney.Language
            }

            const response = await axios.put(`/v2/journey/update-from-journey-management/${updatedJourney.JourneyID}`, payloadAPI, config);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Journey successfully updated'
            }, {root: true});

            return response.data.updatedJourney;
        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Journey failed to be updated'
                }, {root: true});
            }
            throw new Error(err);
        }
    },

    async updateParticipantProgress({ commit, dispatch, rootState }, updatedProgressData) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        // Digistore24 B2C buyers: route to the public ds24 endpoint authorised
        // with the B2C session token. Progress is not tracked in userJourneys
        // (no Cognito user), so return the updated progress to the caller.
        const isB2C = updatedProgressData && updatedProgressData.b2c;

        try {
            let config = {
                headers: {
                    Authorization: isB2C
                        ? `Bearer ${rootState.ds24.sessionToken}`
                        : rootState.auth.bearer
                },
            }

            let payload = {
                "JourneyID": updatedProgressData.JourneyID,
                "StructureItemID": updatedProgressData.StructureItemID,
            }

            const url = isB2C
                ? `/v2/ds24/journey/update-participant-progress`
                : `/v2/journey/update-participant-progress`;

            const response = await axios.put(url, payload, config);

            if (!isB2C) {
                commit('UPDATE_JOURNEY_PROGRESS', response.data.updatedProgress);
            }

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'CONGRATULATIONS on your progress!'
            }, {root: true});

            if (isB2C) {
                return {
                    ...response.data.updatedProgress,
                    certificate: response.data.certificate || null,
                };
            }

            return response.data.updatedProgress;

        }
        catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: 'Failed to update your progress'
            }, {root: true});
            throw new Error(err);
        }
    },

    async updateJourneyUserConsent({ commit, dispatch, rootState }, data) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            let payload = {
                "JourneyParticipantLinkID": data.JourneyParticipantLinkID,
            }

            await axios.post(`/v2/journey/update-journey-user-consent`, payload, config);

            commit('UPDATE_JOURNEY_USER_CONSENT', data.JourneyID);

        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Failed to update your consent'
                }, {root: true});
            }
            throw new Error(err);
        }
    },

    async deleteJourney({ commit, dispatch, rootState }, payload) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            let payloadAPI = {
                "JourneyID": payload.JourneyID,
            }

            await axios.delete(`/v2/journey-delete/${payloadAPI.JourneyID}`, config);

            // TODO fix delete journey on frontend
            commit('DELETE_JOURNEY', payloadAPI.JourneyID);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Journey deleted successfully'
            }, {root: true});

        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Failed to delete the journey'
                }, {root: true});
            }
            throw new Error(err);
        }
    },

    async getUsersLinkedToJourney({ commit, dispatch, rootState }, payload) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            };

            let payloadAPI = {
                JourneyID: payload.JourneyID,
                // IsFacilitatorView: payload.IsFacilitatorView,
            };

            const response = await axios.post('/v2/get-users-linked-to-journey', payloadAPI, config);

            response.data.linkedUsers.forEach((user) => {
                user.Exists = true;
            });

            return response.data.linkedUsers;
        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            throw new Error(err);
        }
    },

    async getParticipantJourneyProgress({ dispatch, rootState }, { journeyID, participantID }) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
        const config = {
            headers: { Authorization: rootState.auth.bearer },
        };
        try {
            const response = await axios.post('/v2/journey/get-participant-progress', {
                JourneyID: journeyID,
                ParticipantID: participantID,
            }, config);
            return response.data;
        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            throw err;
        }
    },

    // Author / Co-Author: the journey's assignments with the correct answers and every participant's submitted answers
    async getJourneyAssignmentsAnswers({ dispatch, rootState }, { journeyID }) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
        const config = {
            headers: { Authorization: rootState.auth.bearer },
        };
        try {
            // the API pages the participants (a response is limited in size), the pages are merged here so the page
            // gets everything at once. The journey info and the assignments come with the first page
            let result = null;
            let cursor = 0;

            do {
                const response = await axios.post('/v2/journey/get-assignments-answers', {
                    JourneyID: journeyID,
                    Cursor: cursor,
                }, config);

                if (result === null) {
                    result = { ...response.data, participants: [...(response.data.participants || [])] };
                } else {
                    result.participants.push(...(response.data.participants || []));
                }

                cursor = response.data.nextCursor;
            } while (cursor !== null && cursor !== undefined);

            delete result.nextCursor;
            return result;
        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            throw err;
        }
    },

    // Author / Co-Author saves a batch of grades on written answers ([{ JourneyParticipantLinkID, AssignmentID, QuizID, ItemID, HasPassed }]),
    // returns one result per participant ({ JourneyParticipantLinkID, success, submissions, isLockStateChanged } or the error flag)
    async gradeWrittenAnswers({ dispatch, rootState }, { journeyID, grades }) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
        const config = {
            headers: { Authorization: rootState.auth.bearer },
        };
        try {
            const response = await axios.post('/v2/journey/grade-written-answers', {
                JourneyID: journeyID,
                Grades: grades,
            }, config);
            return response.data.results;
        }
        catch (err) {
            const data = err.response?.data;

            // an expired account gets the account expired dialog from the page, no toast
            if (data?.expiredAccount) {
                throw err;
            }

            let messageKey = 'assignmentAnswers.gradeError';

            if (data?.notAllowedInJourney) {
                messageKey = 'notifications.notAllowedInJourney';
            } else if (data?.writtenAnswerNotFound) {
                messageKey = 'assignmentAnswers.writtenAnswerNotFound';
            }

            dispatch('notifications/addNotification', {
                type: 'error',
                messageKey,
            }, {root: true});
            throw err;
        }
    },

    async getMyJourneyRole({ rootState }, { journeyID }) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
        const config = {
            headers: { Authorization: rootState.auth.bearer },
        };
        const response = await axios.post('/v2/journey/get-my-journey-role', {
            JourneyID: journeyID,
        }, config);
        return response.data.JourneyRole;
    },

    // Same expiration checks the save runs (expiredAccount / expiredLinkedUsers), done on JourneyCreation enter.
    // No journeyID = create mode
    async checkJourneyEditEligibility({ rootState }, { journeyID } = {}) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
        const config = {
            headers: { Authorization: rootState.auth.bearer },
        };
        const response = await axios.post('/v2/journey/check-edit-eligibility', {
            JourneyID: journeyID,
        }, config);
        return response.data;
    },

    // Basic journey info only (ID, Name, Description, Active, Language, Picture, JourneyRole), no content
    async getJourneySummary({ rootState }, { journeyID }) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
        const config = {
            headers: { Authorization: rootState.auth.bearer },
        };
        const response = await axios.post('/v2/journey/fetch-journey-summary', {
            JourneyID: journeyID,
        }, config);
        return response.data.journey;
    },

    async addUserToJourney({ commit, dispatch, rootState }, payload) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        let formattedNewUsers = [];

        if (payload.NewUsers.length > 0) {
            payload.NewUsers.forEach((newUser) => {
                let formattedNewUser = {
                    CognitoAttributes: {
                        given_name: newUser.given_name || '',
                        family_name: newUser.family_name || '',
                        email: newUser.email,
                        address: newUser.address || '',
                        'custom:position': newUser['custom:position'] || '',
                    },
                    Note: newUser.Note || '',
                    JourneyRole: newUser.JourneyRole
                }

                formattedNewUsers.push(formattedNewUser);
            })
        }

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            let payloadAPI = {
                "NewUsers": formattedNewUsers,
                "JourneyID": payload.JourneyID,
                "IsAlignExpirationDateToYearLongActive": payload.IsAlignExpirationDateToYearLongActive,
                // "IsAlignExpirationDateToHalfYearLongActive": payload.IsAlignExpirationDateToHalfYearLongActive,
                "SkipLinkUserToJourney": payload.SkipLinkUserToJourney,
            };

            if (payload.emailTemplateId) {
                payloadAPI.EmailTemplateId = payload.emailTemplateId;
            }

            await axios.post('/v2/add-user-to-journey', payloadAPI, config);

            // participants and observers are both added from drafts
            if ((payload.JourneyRole === 'Participant' || payload.JourneyRole === 'Observer') && !payload.SkipLinkUserToJourney) {

                let updatedDraftUsers = payload.AllDraftUsers.filter(obj1 =>
                    !payload.NewUsers.some(obj2 => obj1.tempID === obj2.tempID)
                );

                let payloadAPI2 = {
                    JourneyID: payload.JourneyID,
                    DraftUsers: updatedDraftUsers,
                };

                await dispatch('updateDraftUsersToJourney', payloadAPI2);

            }

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Users successfully added to journey'
            }, {root: true});

        }
        catch (err) {
            // expired-accounts business error carries its own dialog (handled by the caller); skip the generic toast
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else if (err.response?.data?.journeyRoleNotAllowed) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.journeyRoleNotAllowed'
                }, {root: true});
            }
            else if (err.response?.data?.coAuthorNotFacilitator) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.coAuthorNotFacilitator'
                }, {root: true});
            }
            else if (err.response?.data?.userAlreadyInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.userAlreadyInJourney'
                }, {root: true});
            }
            else if (err.response?.data?.observersLimitReached) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.observersLimitReached'
                }, {root: true});
            }
            else if (!err.response?.data?.expiredAccounts && !err.response?.data?.expiredAccount) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: err.response?.data?.message || 'Some Participants failed to be registered'
                }, {root: true});
            }

            throw err;
        }
    },

    async removeUsersFromJourney({commit, dispatch, rootState}, payload) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            let payloadAPI = {
                "RemovedUsersParticipantLinkIDs": payload.RemovedUsersParticipantLinkIDs,
                "JourneyID": payload.JourneyID,
            };

            await axios.post('/v2/remove-users-from-journey', payloadAPI, config);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Users removed successfully'
            }, {root: true});

        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else if (err.response?.data?.authorRemovalNotAllowed) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.authorRemovalNotAllowed'
                }, {root: true});
            }
            else if (!err.response?.data?.expiredAccount) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Failed to remove users from journey'
                }, {root: true});
            }
            throw err;
        }
    },

    // sets new temporary passwords without sending any email and returns them, so the author can send them from their own email client
    async resetParticipantsTemporaryPassword({commit, dispatch, rootState}, payload) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            let payloadAPI = {
                "JourneyID": payload.JourneyID,
                "ParticipantIDs": payload.ParticipantIDs,
            };

            const response = await axios.post('/v2/journey/reset-participants-temporary-password', payloadAPI, config);

            return response.data;
        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Only the author of this journey can reset temporary passwords'
                }, {root: true});
            }
            else if (!err.response?.data?.expiredAccount) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'User temporary password failed to reset'
                }, {root: true});
            }
            throw err;
        }
    },

    async updateParticipantByAuthor({commit, dispatch, rootState}, payload) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            let payloadAPI = {
                JourneyParticipantLinkID: payload.JourneyParticipantLinkID,
                JourneyID: payload.JourneyID,
                JourneyRole: payload.JourneyRole,
                Note: payload.Note,
                ExtraPermissions: payload.ExtraPermissions,
                sub: payload.sub,
                given_name: payload.given_name,
                family_name: payload.family_name,
                address: payload.address,
                'custom:position': payload['custom:position'],
            };

            await axios.post('/v2/update-participant-by-author', payloadAPI, config);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'User successfully updated'
            }, {root: true});

        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else if (err.response?.data?.journeyRoleChangeNotAllowed) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.journeyRoleChangeNotAllowed'
                }, {root: true});
            }
            else if (!err.response?.data?.expiredAccount) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Failed to update user'
                }, {root: true});
            }
            throw err;
        }
    },

    // replaces the Participants (user ids) an Observer follows in a journey
    async updateObserverLinkedUsers({dispatch, rootState}, payload) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            let payloadAPI = {
                JourneyID: payload.JourneyID,
                ObserverLinkID: payload.ObserverLinkID,
                LinkedUsersIDs: payload.LinkedUsersIDs,
            };

            const response = await axios.post('/v2/journey/update-observer-linked-users', payloadAPI, config);

            return response.data;
        }
        catch (err) {
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else if (err.response?.data?.observerLinkNotFound) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.observerLinkNotFound'
                }, {root: true});
            }
            else if (err.response?.data?.linkedUserNotParticipant) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.linkedUserNotParticipant'
                }, {root: true});
            }
            else if (!err.response?.data?.expiredAccount) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.observerLinkedUsersUpdateFailed'
                }, {root: true});
            }
            throw err;
        }
    },

    async getJourneysImages({commit, dispatch, rootState}) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            let payloadAPI = {

            };

            const response = await axios.post('/v2/list-journeys-images', payloadAPI, config);

            return response.data.assetsKeys;

        }
        catch (err) {
            throw new Error(err);
        }
    },

    async getUserJourneyProgress({commit, dispatch, state, rootState}, journeyID) {
        const journey = state.userJourneys.find((userJourney) => userJourney.ID === journeyID);

        if (journey.hasOwnProperty('ParticipantProgress')) {
            return {
                ParticipantProgress: journey.ParticipantProgress,
                ParticipantProgressPercentage: journey.ParticipantProgressPercentage,
            }
        }
        return null;
    },

    async submitAssignment({commit, dispatch, state, rootState}, payload) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        // Digistore24 B2C buyer: route to the public ds24 endpoint with the B2C
        // session token and return the updated journey directly (no userJourneys).
        const isB2C = payload && payload.b2c;

        try {
            let config = {
                headers: {
                    Authorization: isB2C
                        ? `Bearer ${rootState.ds24.sessionToken}`
                        : rootState.auth.bearer
                },
            }

            let payloadAPI = {
                SubmittedStructureItemID: payload.SubmittedStructureItemID,
                Journey: payload.Journey,
            };

            const url = isB2C
                ? '/v2/ds24/journey/submit-assignment'
                : '/v2/journey/submit-assignment';

            let response = await axios.post(url, payloadAPI, config);

            let updatedJourney = response.data.journey;

            if (isB2C) {
                dispatch('notifications/addNotification', {
                    type: 'success',
                    message: 'Assignment submitted successfully'
                }, {root: true});
                return updatedJourney;
            }

            if (!payload.IsJourneyReusableTemplatePage) {
                // commit('UPDATE_USER_JOURNEY', {
                //     Journey: updatedJourney
                // });
                // SET_USER_JOURNEY also adds the journey when it is not in userJourneys yet, so the return below is never undefined
                commit('SET_USER_JOURNEY', updatedJourney);

                dispatch('notifications/addNotification', {
                    type: 'success',
                    message: 'Assignment submitted successfully'
                }, {root: true});

                dispatch('userNotifications/fetchUserNotifications', {

                },{root: true});

                const indexUserJourney = state.userJourneys.findIndex((userJourney) => userJourney.ID === payload.Journey.ID);

                // JourneyView fast-loads from lastUserJourney on refresh, so point it to the journey
                // the page now holds, otherwise the pre-submit progress is restored
                if (state.lastUserJourney && state.lastUserJourney.ID === payload.Journey.ID) {
                    commit('SET_LAST_USER_JOURNEY', state.userJourneys[indexUserJourney] ?? updatedJourney);
                }

                return state.userJourneys[indexUserJourney];
            }
            else {
                return updatedJourney;
            }
        }
        catch (err){
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Assignment failed to be submitted'
                }, {root: true});
            }
        }
    },
    async saveAssignmentResponse({commit, dispatch, state, rootState}, payload) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        const isB2C = payload && payload.b2c;

        try {
            let config = {
                headers: {
                    Authorization: isB2C
                        ? `Bearer ${rootState.ds24.sessionToken}`
                        : rootState.auth.bearer
                },
            }

            let payloadAPI = {
                SubmittedStructureItemID: payload.SubmittedStructureItemID,
                Journey: payload.Journey,
            };

            const url = isB2C
                ? '/v2/ds24/journey/save-assignment-response'
                : '/v2/journey/save-assignment-response';

            let response = await axios.post(url, payloadAPI, config);

            let updatedJourney = response.data.journey;

            if (isB2C) {
                dispatch('notifications/addNotification', {
                    type: 'success',
                    message: 'Assignment saved successfully'
                }, {root: true});
                return updatedJourney;
            }

            // commit('UPDATE_USER_JOURNEY', {
            //     Journey: updatedJourney
            // });
            // SET_USER_JOURNEY also adds the journey when it is not in userJourneys yet, so the return below is never undefined
            commit('SET_USER_JOURNEY', updatedJourney);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Assignment saved successfully'
            }, {root: true});

            const indexUserJourney = state.userJourneys.findIndex((userJourney) => userJourney.ID === payload.Journey.ID);

            // JourneyView fast-loads from lastUserJourney on refresh, so point it to the journey
            // the page now holds, otherwise the pre-save answers are restored
            if (state.lastUserJourney && state.lastUserJourney.ID === payload.Journey.ID) {
                commit('SET_LAST_USER_JOURNEY', state.userJourneys[indexUserJourney] ?? updatedJourney);
            }

            return state.userJourneys[indexUserJourney];
        }
        catch (err){
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            // a submitted assignment that can't be reset can't be saved as a draft anymore (the save would reset it)
            else if (err.response?.data?.assignmentAlreadySubmitted) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'journey.assignmentAlreadySubmitted'
                }, {root: true});
            }
            else {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Assignment failed to be saved'
                }, {root: true});
            }
        }
    },

    async updateDraftUsersToJourney ({commit, dispatch, state, rootState}, payload) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            payload.DraftUsers.forEach((user) => {
                if (user.hasOwnProperty('IsSelected')) {
                    delete user.IsSelected;
                }
            });

            let payloadAPI = {
                JourneyID: payload.JourneyID,
                DraftUsers: payload.DraftUsers,
            };

            await axios.post('/v2/journey/update-draft-users-to-journey', payloadAPI, config);

            commit('UPDATE_DRAFT_USERS', payloadAPI);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: 'Journey draft users successfully updated'
            }, {root: true});
        }
        catch (err) {
            if (err.response?.data?.expiredAccount) {
                throw err;
            }
            if (err.response?.data?.notAllowedInJourney) {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    messageKey: 'notifications.notAllowedInJourney'
                }, {root: true});
            }
            else {
                dispatch('notifications/addNotification', {
                    type: 'error',
                    message: 'Journey draft users failed to be updated'
                }, {root: true});
            }
        }
    },

    async authorJourneyLink({ commit, dispatch, rootState }, payload) {
        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            }

            let payloadAPI = {
                "JourneyID": payload.JourneyID,
            }

            const response = await axios.post(`/v2/journey/author-journey-link`, payloadAPI, config);

            dispatch('notifications/addNotification', {
                type: 'success',
                message: response.data.message
            }, {root: true});

            // return response.data.updatedJourney;
        }
        catch (err) {
            dispatch('notifications/addNotification', {
                type: 'error',
                message: 'Journey author failed to be updated'
            }, {root: true});
            throw new Error(err);
        }
    },

    resetState({ commit }) {
        commit('RESET_STATE');
    },

    async getUserJourneys({ commit, rootState, dispatch }) {
        // Try to refresh the token first
        try {
            await getAuthToken();
        } catch (error) {
            console.error('Failed to refresh token before fetching journeys:', error);
            // Continue anyway and let the API call handle any auth issues
        }

        axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;

        try {
            commit('loading/setLoading', true, { root: true });

            let config = {
                headers: {
                    Authorization: rootState.auth.bearer
                },
            };

            // Add a timestamp to prevent caching
            const timestamp = new Date().getTime();
            config.params = { _t: timestamp };

            const response = await axios.post('/v2/journey/list-user-journeys', {}, config);

            commit('PUSH_USER_JOURNEYS', response.data.journeys);
            commit('loading/setLoading', false, { root: true });
        }
        catch (err) {
            console.error('Error fetching user journeys:', err);
            commit('loading/setLoading', false, { root: true });
            
            dispatch('notifications/addNotification', {
                type: 'error',
                message: 'Failed to fetch journeys'
            }, {root: true});
        }
    },

    // async sendJourneysReminders({commit, dispatch, rootState}) {
    //     axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    //
    //     try {
    //         let config = {
    //             headers: {
    //                 Authorization: rootState.auth.bearer
    //             },
    //         };
    //
    //         let payloadAPI = {
    //
    //         };
    //
    //         await axios.post(`/v2/journey/send-journeys-reminders`, payloadAPI, config);
    //
    //         dispatch('notifications/addNotification', {
    //             type: 'success',
    //             message: 'Notification(s) sent successfully'
    //         }, {root: true});
    //
    //     }
    //     catch (err) {
    //         dispatch('notifications/addNotification', {
    //             type: 'error',
    //             message: 'Failed to send notification(s)'
    //         }, {root: true});
    //     }
    // },

}

const mutations = {
    SET_USER_JOURNEYS(state, journeys) {
        state.userJourneys = journeys;
    },
    // SET_LAST_OPENED_FULL_JOURNEY(state, { journeyId, journey, tenantId, cachedAt }) {
    //     state.lastOpenedFullJourney = {
    //         journeyId: journeyId ?? null,
    //         journey: journey ?? null,
    //         tenantId: tenantId ?? null,
    //         cachedAt: cachedAt ?? new Date().toISOString(),
    //     };
    // },
    // CLEAR_LAST_OPENED_FULL_JOURNEY(state) {
    //     state.lastOpenedFullJourney = createEmptyLastOpenedFullJourney();
    // },
    SET_USER_JOURNEY(state, updatedJourney) {

        const indexUserJourney = state.userJourneys.findIndex(journey => journey.ID === updatedJourney.ID);

        if (indexUserJourney !== -1) {

            state.userJourneys[indexUserJourney] = updatedJourney;

            // for (let key in updatedJourney) {
            //     if (key in state.userJourneys[indexUserJourney]) {
            //         state.userJourneys[indexUserJourney][key] = updatedJourney[key];
            //     }
            // }
        }
        else {
            state.userJourneys.push(updatedJourney);
        }
    },
    SET_LAST_USER_JOURNEY(state, lastUserJourney) {
        state.lastUserJourney = lastUserJourney;
    },
    SET_LAST_UPDATED(state, timestamp) {
        state.lastUpdated = timestamp;
    },
    ADD_JOURNEY(state, newJourney) {
        state.userJourneys.push(newJourney);
    },
    UPDATE_JOURNEY(state, updatedJourney) {
        const indexUserJourney = state.userJourneys.findIndex(journey => journey.ID === updatedJourney.ID);

        if (indexUserJourney !== -1) {
            for (let key in updatedJourney) {
                if (key in state.userJourneys[indexUserJourney]) {
                    state.userJourneys[indexUserJourney][key] = updatedJourney[key];
                }
            }
        }
    },
    UPDATE_JOURNEY_ACTIVE_STATUS(state, updatedJourney) {
        const indexUserJourneys = state.userJourneys.findIndex(journey => journey.ID === updatedJourney.ID);

        if (indexUserJourneys !== -1) {
            state.userJourneys[indexUserJourneys].Active = updatedJourney.Active;
        }
    },
    UPDATE_JOURNEY_HIDE_FROM_TENANT_ADMIN(state, updatedJourney) {
        const indexUserJourneys = state.userJourneys.findIndex(journey => journey.ID === updatedJourney.ID);

        if (indexUserJourneys !== -1) {
            state.userJourneys[indexUserJourneys].IsHideFromTenantAdmin = updatedJourney.IsHideFromTenantAdmin;
        }
    },
    UPDATE_JOURNEY_ANONYMOUS_PARTICIPANT_MODE(state, updatedJourney) {
        const indexUserJourneys = state.userJourneys.findIndex(journey => journey.ID === updatedJourney.ID);

        if (indexUserJourneys !== -1) {
            state.userJourneys[indexUserJourneys].IsAnonymousParticipantMode = updatedJourney.IsAnonymousParticipantMode;
        }
    },
    UPDATE_JOURNEY_PROGRESS(state, updatedProgress) {
        const indexUserJourneys = state.userJourneys.findIndex(journey => journey.ID === updatedProgress.JourneyID);

        if (indexUserJourneys !== -1) {
            state.userJourneys[indexUserJourneys].ParticipantProgress = updatedProgress.ParticipantProgress;
            state.userJourneys[indexUserJourneys].ParticipantProgressPercentage = updatedProgress.ParticipantProgressPercentage;

            // Keep Structure items in sync so future deep-clones have the correct PersonalUnlockDate
            if (Array.isArray(updatedProgress.ParticipantProgress) && Array.isArray(state.userJourneys[indexUserJourneys].Structure)) {
                updatedProgress.ParticipantProgress.forEach((progressItem) => {
                    if (progressItem.PersonalUnlockDate) {
                        const structureItem = state.userJourneys[indexUserJourneys].Structure.find((s) => s.ID === progressItem.StructureItemID);
                        if (structureItem) {
                            structureItem.Periods.PersonalUnlockDate = progressItem.PersonalUnlockDate;
                        }
                    }
                });
            }
        }
    },
    UPDATE_JOURNEY_USER_CONSENT(state, JourneyID) {
        const indexUserJourneys = state.userJourneys.findIndex(journey => journey.ID === JourneyID);

        if (indexUserJourneys !== -1) {
            state.userJourneys[indexUserJourneys].Consent = true;
        }
    },
    UPDATE_USER_JOURNEY(state, payload) {
        const indexUserJourney = state.userJourneys.findIndex((userJourney) => userJourney.ID === payload.Journey.ID);

        if (indexUserJourney !== -1) {
            state.userJourneys[indexUserJourney] = payload.Journey;
        }
    },
    DELETE_JOURNEY(state, deletedJourneyID) {
        state.userJourneys = state.userJourneys.filter((userJourney) => userJourney.ID !== deletedJourneyID);
        if (state.lastUserJourney && String(state.lastUserJourney.ID) === String(deletedJourneyID)) {
            state.lastUserJourney = null;
        }
    },
    SET_PICTURE(state, picture) {
        // state.userInfo.picture = picture;
        // state.user.attributes.picture = picture;
    },
    UPDATE_PARTICIPANT_ASSIGNMENT(state, payload) {
        const indexUserJourneys = state.userJourneys.findIndex(journey => journey.ID === payload.JourneyID);
        if (indexUserJourneys !== -1
        && state.userJourneys[indexUserJourneys].Structure[payload.indexStructureItem] !== undefined
        && state.userJourneys[indexUserJourneys].Structure[payload.indexStructureItem].Tasks[payload.indexTask] !== undefined) {
            state.userJourneys[indexUserJourneys].Structure[payload.indexStructureItem].Tasks[payload.indexTask].IsDone = payload.Task.IsDone;
        }
    },
    UPDATE_DRAFT_USERS(state, payload) {
        const indexUserJourneys = state.userJourneys.findIndex(journey => journey.ID === payload.JourneyID);
        if (indexUserJourneys !== -1) {
            state.userJourneys[indexUserJourneys].DraftUsers = payload.DraftUsers;
        }
    },
    RESET_STATE(state) {
        Object.assign(state, getDefaultState());
    },
    PUSH_USER_JOURNEYS(state, journeys) {
        state.userJourneys = journeys;
    },
    SET_IS_PARTICIPANT_VIEW(state, isParticipantView) {
        state.isParticipantView = isParticipantView;
    },

};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
